export const PROVIDERS = ['padi', 'ssi', 'sdi', 'naui', 'raid'] as const

export type AccreditationProvider = (typeof PROVIDERS)[number]

export const PROVIDER_LABELS: Record<AccreditationProvider, string> = {
  padi: 'PADI',
  ssi: 'SSI',
  sdi: 'SDI',
  naui: 'NAUI',
  raid: 'RAID'
}

export const LEVELS_BY_PROVIDER: Record<AccreditationProvider, string[]> = {
  padi: [
    'Discover Scuba Diving',
    'Scuba Diver',
    'Open Water Diver',
    'Advanced Open Water Diver',
    'Rescue Diver',
    'Master Scuba Diver',
    'Divemaster'
  ],
  ssi: [
    'Try Scuba / Basic Diver',
    'Scuba Diver',
    'Open Water Diver',
    'Advanced Adventurer',
    'Specialty Diver',
    'Advanced Open Water Diver',
    'Diver Stress & Rescue',
    'Master Diver',
    'Dive Guide / Divemaster'
  ],
  sdi: [
    'Future Buddies',
    'Scuba Diver',
    'Open Water Scuba Diver',
    'Advanced Adventure Diver',
    'Advanced Scuba Diver',
    'Rescue Diver',
    'Master Scuba Diver',
    'Divemaster'
  ],
  naui: [
    'Try Scuba / Passport Diver',
    'Scuba Diver',
    'Advanced Scuba Diver',
    'Master Scuba Diver',
    'Scuba Rescue Diver',
    'Divemaster'
  ],
  raid: [
    'Try Dive',
    'Scuba Diver',
    'Open Water 20',
    'Explorer 30',
    'Advanced 35',
    'Master Rescue',
    'Master Diver',
    'Divemaster'
  ]
}

/** Returns the valid levels for a given provider, or empty array for unknown providers. */
export function getLevelsForProvider(provider: string): string[] {
  return LEVELS_BY_PROVIDER[provider as AccreditationProvider] ?? []
}

/** Returns true if the given level is valid for the given provider. */
export function isValidLevelForProvider(
  provider: string,
  level: string
): boolean {
  const levels = getLevelsForProvider(provider)
  return levels.includes(level)
}

/**
 * Maps each provider to its Divemaster-equivalent level string.
 * Intentionally separate from LEVELS_BY_PROVIDER so that adding a new level
 * to a provider does not automatically confer Divemaster eligibility.
 */
const DIVEMASTER_LEVELS: Record<AccreditationProvider, string> = {
  padi: 'Divemaster',
  ssi: 'Dive Guide / Divemaster',
  sdi: 'Divemaster',
  naui: 'Divemaster',
  raid: 'Divemaster'
}

/** Returns true if the given provider+level combination qualifies as Divemaster. */
export function isDivemasterLevel(provider: string, level: string): boolean {
  const dmLevel = DIVEMASTER_LEVELS[provider as AccreditationProvider]
  return dmLevel !== undefined && dmLevel === level
}

/** Providers that require registeredEmail */
export const PROVIDERS_REQUIRING_EMAIL: AccreditationProvider[] = ['padi']

/** Returns true if the provider requires a registeredEmail field. */
export function providerRequiresEmail(provider: string): boolean {
  return PROVIDERS_REQUIRING_EMAIL.includes(provider as AccreditationProvider)
}
